import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import {
  ChevronDown,
  ChevronRight,
  CopyIcon,
  DownloadIcon,
} from "lucide-react";
import { type HistoryList, type OldResponse } from "@/types";
import { aiApi } from "@/api/aiApi.ts";
import { useTranslation } from "@/locales";

import TypewriterMarkdown from "@/components/TypewriterMarkdown";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/16/solid";
import { toBlob, toPng } from "html-to-image";
import { toast } from "sonner";

interface ShareImgDialogProps {
  isOpen: boolean;
  topicNo?: string;
  shareIds: string[];
  onClose: () => void;
}

const ShareImgDialog = ({
  isOpen,
  topicNo,
  shareIds,
  onClose,
}: ShareImgDialogProps) => {
  const { t } = useTranslation();
  const targetRef = useRef(null);
  const [isConverting, setIsConverting] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const [share, setShare] = useState<OldResponse>({
    records: [],
    topicName: "",
    topicNo: "",
  });

  // 将元素内容转换为图片
  const convertElementToImage = async () => {
    console.log(targetRef.current);
    console.log(isConverting);
    if (!targetRef.current || isConverting) return;

    try {
      setIsConverting(true);
      setIsCopied(false);

      // 添加平滑的转换动画
      targetRef.current.style.transition = "all 0.3s";
      targetRef.current.style.boxShadow = "0 0 15px rgba(60, 140, 231, 0.5)";

      // 等待转换完成
      const dataUrl = await toPng(targetRef.current, {
        backgroundColor: "#ffffff",
        pixelRatio: 2, // 高清图片
        cacheBust: true, // 避免缓存问题
      });

      setTimeout(() => {
        targetRef.current.style.boxShadow = "0 0 5px rgba(0,0,0,0.1)";
        setImageData(dataUrl);
        setIsConverting(false);
      }, 300);
      console.log(t("common.conversionCompleted"));
    } catch (error) {
      console.error(t("common.conversionFailed"), error);
      setIsConverting(false);
      alert(t("common.imageConversionFailed"));
    }
  };

  // 下载图片
  const downloadImage = () => {
    console.log(imageData);
    if (!imageData) return;

    const link = document.createElement("a");
    link.download = `${t("share.shareConversation")}.png`;
    link.href = imageData;
    link.click();
    onClose();

    toast.success(t("common.imageSaved"));
  };

  // 复制图片到剪贴板
  const copyToClipboard = async () => {
    console.log(imageData);
    if (!imageData) return;

    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      onClose();

      toast.success(t("common.imageCopied"));
    } catch (error) {
      console.error(t("common.copyFailed"), error);
      alert(t("common.copyImageFailed"));
    }
  };

  const getShare = async () => {
    if (!topicNo) return;
    const response = (await aiApi.conversation.getTopic(
      topicNo
    )) as OldResponse;
    const list: HistoryList[] = [];
    if (response) {
      if (response.records?.length) {
        response.records.map((item) => {
          if (shareIds.includes(item.conversationNo)) {
            list.push(item);
          }
        });
      }
      setShare({
        ...response,
        records: list,
      });
      convertElementToImage();
    }
  };
  useEffect(() => {
    getShare();
  }, [topicNo, shareIds, isOpen]);

  function copyImg() {
    copyToClipboard();
  }

  function downloadImg() {
    downloadImage();
  }
  const historyThink = (event: HistoryList, index: number) => {
    const changeOpen = () => {
      setImageData(null);
      setShare((prev) => {
        if (!prev.records) return prev;

        const newRecords = [...prev.records];
        newRecords[index] = {
          ...newRecords[index],
          isOpen: !newRecords[index].isOpen,
        };

        return { ...prev, records: newRecords };
      });
      setTimeout(() => {
        convertElementToImage();
      }, 300);
    };
    const thinksStop =
      event.stopReason != "COMPLETE" &&
      !event.answer &&
      event.reasoning.length > 0;

    if (event.isOpen) {
      return (
        <div className="pt-0 ai overflow-hidden bg-[var(--background-page_background_web)]">
          <div
            className={
              "think-button-collapse collapse-collapse-button-Lf stick-LY h-full min-h-[24px]"
            }
          >
            <div
              className="flex justify-between items-center w-full"
              onClick={(e) => {
                e.stopPropagation();
                changeOpen();
              }}
            >
              <div className="think-block-title">
                {thinksStop
                  ? t("chat.thinkingStopped")
                  : t("chat.thinkingCompleted")}
              </div>
              <Button
                variant="outline"
                className="z-10 text-[22px] h-[28px] w-[28px]"
              >
                <ArrowsPointingInIcon
                  className={"fill-[var(--text-text_primary)]"}
                />
              </Button>
            </div>
          </div>
          <div
            className={"think think-container"}
            style={{ maxHeight: "100%" }}
          >
            <div className="content think-content">
              <TypewriterMarkdown
                content={event.reasoning}
                speed={10}
                citations={event.citations}
                docs={event.files}
                conversationNo={event.conversationNo}
              />
            </div>
          </div>
          {/*<div ref={bottomRef}/>*/}
        </div>
      );
    } else {
      return (
        <Button
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            changeOpen();
          }}
        >
          <div>
            <span>
              {thinksStop
                ? t("chat.thinkingStopped")
                : t("chat.thinkingCompleted")}
            </span>
          </div>
          <ArrowsPointingOutIcon
            className={"fill-[var(--text-text_primary)]"}
          />
        </Button>
      );
    }
  };
  const getCurrentDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0"); // 月份从0开始，需要+1
    const day = now.getDate().toString().padStart(2, "0");
    console.log(t("common.dateFormat", { year, month, day }));
    return t("common.dateFormat", { year, month, day });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={"share-img-content"}>
        <DialogHeader>
          <DialogTitle>{t("share.imagePreview")}</DialogTitle>
        </DialogHeader>
        <div
          className={`w-[${720 - 18}px-] flex-1 flex share-img-content-list`}
        >
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1">
              <div className="ai-chat-container flex-1  bg-background pt-0 pb-1.5 flex justify-between flex-col">
                <div
                  className={
                    "flex-1 w-full overflow-y-auto pl-auto pr-auto flex flex-col items-center"
                  }
                >
                  <div className="messages" ref={targetRef}>
                    <div className="conversation-group">
                      <div className={"share_title"}>
                        <h1> {share.topicName}</h1>
                      </div>
                      <div
                        className="pb-[36px] border-solid border-transparent border-b-[0.5px] border-b-[var(--s-color-border-secondary)]  text-[var(--s-color-text-quaternary)] ml-[10px]"
                        style={{ font: "var(--s-font-xs)" }}
                      >
                        <span className={"data"}>{getCurrentDate()}</span>
                        <span className={"mx-[4px]"}>•</span>
                        <span>{t("share.contentGeneratedByAI")}</span>
                      </div>
                    </div>

                    {share.records?.map((record, index) => {
                      return (
                        <div
                          key={record.conversationNo}
                          className="conversation-group "
                        >
                          {/* 问题展示 */}
                          <div className={"flex justify-end flex-1"}>
                            <div className={"flex-1"}>
                              <div className="question-text">
                                <div className="question-text-content">
                                  {record.query}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={"flex justify-between flex-1"}>
                            <div className={"flex-1"}>
                              {/* 渲染事件 */}
                              {record.reasoning.length > 0 ? (
                                <div className={""}>
                                  {historyThink(record, index)}
                                </div>
                              ) : (
                                ""
                              )}
                              <div className="message answer">
                                <div className="content answer-content">
                                  <TypewriterMarkdown
                                    content={record.answer}
                                    speed={10}
                                    citations={record.citations}
                                    docs={record.files}
                                    conversationNo={record.conversationNo}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="inner"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <div className={"flex justify-center flex-1"}>
            <Button
              variant="ghost"
              disabled={!imageData}
              className={
                "border-[1px] border-solid border-[var(--s-color-border-tertiary)]"
              }
              onClick={copyImg}
            >
              <CopyIcon></CopyIcon> {t("share.copyImage")}
            </Button>
            <Button
              variant="ghost"
              disabled={!imageData}
              className={
                "ml-[12px] bg-[#0057ff] hover:opacity-[0.6] text-[#fff] hover:text-white"
              }
              onClick={downloadImg}
            >
              <DownloadIcon></DownloadIcon> {t("share.downloadImage")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareImgDialog;
